import { action, observable } from 'mobx';
import { AppManager, Variables, ConnectionAppManager } from 'core/index';
import { routerStore } from 'core/routing';
import { modalStore } from 'apps/modals/app';

import { IssueForm } from 'apps/issueTracker/views/issueForm';

import { IssueTrackerQuery, ProjectIssuesQuery } from 'apps/issueTracker/gql';
import { IssueTrackerModel } from 'apps/issueTracker/models';

import { IssueTrackerApp } from './apps/issueTrackerApp';
import { IssueArrayApp } from './apps/issueApp';

export class IssueConnManager extends ConnectionAppManager {
  static ArrayApp = IssueArrayApp;

  @observable sortBy = 'number';

  constructor(parent, init, options) {
    super(parent, init, options);
    this.initialBatchSize =
      init.initialBatchSize || this.arrayApp.cardLayout.initialBatchSize;
    this.batchSize = init.batchSize || this.arrayApp.cardLayout.batchSize;
    this.whitelabels = init.whitelabels || [];
    this.contains = init.contains || [];
    this.variables = new Variables({
      contains: () => this.contains,
      sortBy: () => this.sortBy,
      batchSize: () => {
        if (this.loadedMore) {
          return this.batchSize;
        }
        return this.initialBatchSize;
      },
      cursor: () => this.endCursor,
      space: () =>
        this.params
          ? this.params.get('profile') || this.params.get('initiative')
          : '',
      slug: () => (this.params ? this.params.get('slug') : ''),
      tag: () => init.tag || '',
      filterBy: () => init.filterBy || [],
      whitelabels: () => this.whitelabels,
    });
    this.loadMoreQuery = init.loadMoreQuery || ProjectIssuesQuery;
  }

  @action NewVariables() {
    this.Reset();
  }

  @action SetSort(sort) {
    this.sortBy = sort;
    this.Clear();
    this.loadMore();
  }

  disposeWhen() {
    return !routerStore.route.name.includes('project');
  }
}

export class IssueTrackerAppManager extends AppManager {
  static registryName = 'IssueTrackerAppManager';
  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('issueTracker', IssueTrackerApp);

    this.issuesConn = new IssueConnManager(this, {
      params: this.params,
      filterBy: [['status', 'Open']],
    });

    this.closedIssuesConn = new IssueConnManager(this, {
      params: this.params,
      filterBy: [['status', 'Closed']],
    });

    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
      cursor: () => this.issuesConn.endCursor,
      sortBy: () => this.issuesConn.sortBy,
      batchSize: () => this.issuesConn.batchSize,
      filterBy: () => this.issuesConn.filterBy,
    });

    this.closedIssuesVariables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
      cursor: () => this.closedIssuesConn.endCursor,
      sortBy: () => this.closedIssuesConn.sortBy,
      batchSize: () => this.closedIssuesConn.batchSize,
      filterBy: () => this.closedIssuesConn.filterBy,
    });

    this.variables.onChange(['space', 'slug'], this.NewVariables);
    this.closedIssuesVariables.onChange(
      ['space', 'slug'],
      this.ClosedIssuesNewVariables,
    );
  }

  @action NewVariables = () => {
    this.issuesConn.NewVariables(); // Reset the connection
    this.NewIssueTrackerQuery();
  };

  @action ClosedIssuesNewVariables = () => {
    this.closedIssuesConn.NewVariables();
    this.ClosedIssuesQuery();
  };

  @action NewIssueTrackerQuery = () => {
    if (!(this.variables.space && this.variables.slug)) return;
    const q = new IssueTrackerQuery(this, {
      queryKey: 'issueTracker',
      onSysErr: this.onError,
    });
    q.process(
      issueTracker => IssueTrackerModel.Load(issueTracker),
      this.IssueTrackerUpdate,
    );
    q.process(this.issuesConn.process, this.IssuesUpdate, {
      subPath: 'issues',
    });

    q.query();
  };

  @action ClosedIssuesQuery = () => {
    this.closedIssuesConn.loadMore();
  };

  @action OpenIssueForm = () => {
    modalStore.SetModalComponent(IssueForm, {
      parent: this,
      issueTracker: this.issueTracker,
    });
  };

  @action IssueTrackerUpdate = issueTrackerModel => {
    this.issueTracker = issueTrackerModel;
  };

  @action IssuesUpdate = (issues, query) => {
    if (issues !== null) {
      this.issuesConn.pushResultIfFirst(issues, query);
    }
  };

  @action ClosedIssuesUpdate = (issues, query) => {
    if (issues !== null) {
      this.closedIssuesConn.pushResultIfFirst(issues, query);
    }
  };
}
